import type { QueryClient } from "@tanstack/solid-query";
import { invalidateReportsQueries } from "~/features/reports/reports.invalidation";
import { updateListItemInCache } from "~/lib/optimisticCache";
import { createResource } from "~/lib/resourceFactory";
import { getJournalEntry, listJournalEntries } from "./journal-entry.api";
import type { JournalEntryItem } from "./journal-entry.server";

type JournalEntriesPage = Awaited<ReturnType<typeof listJournalEntries>>;
type JournalEntryDetail = Awaited<ReturnType<typeof getJournalEntry>>;

export type JournalEntriesParams = {
  businessId: string;
  search?: string;
  accountId?: string;
  offset?: number;
  limit: number;
  dateFrom?: string;
  dateTo?: string;
  ids?: string[];
  classIds?: string[];
  source?: "manual" | "bank";
  sortBy?: string;
  sortDir?: "asc" | "desc";
};

export type JournalEntryDetailParams = { businessId: string; id: string };

export const JournalEntriesResource = createResource<
  JournalEntriesPage,
  JournalEntriesParams
>({
  key: (p) =>
    [
      "journal-entry",
      p.businessId,
      p.limit,
      p.offset ?? 0,
      p.search ?? "",
      p.accountId ?? "",
      p.dateFrom ?? "",
      p.dateTo ?? "",
      p.ids === undefined ? "*" : p.ids.join(","),
      p.classIds === undefined ? "*" : p.classIds.join(","),
      p.source ?? "",
      p.sortBy ?? "",
      p.sortDir ?? "",
    ] as const,
  fetcher: (p) =>
    listJournalEntries({
      data: {
        businessId: p.businessId,
        search: p.search,
        accountId: p.accountId,
        offset: p.offset,
        limit: p.limit,
        dateFrom: p.dateFrom,
        dateTo: p.dateTo,
        ids: p.ids,
        classIds: p.classIds,
        source: p.source,
        sortBy: p.sortBy,
        sortDir: p.sortDir,
      },
    }),
  // Avoid suspending SSR when the query is disabled (no active business).
  placeholderData: {
    items: [],
    total: 0,
    debitTotal: 0,
    classCounts: [],
    sourceCounts: [],
  },
});

export const JournalEntryDetailResource = createResource<
  JournalEntryDetail,
  JournalEntryDetailParams
>({
  key: ({ businessId, id }) => ["journal-entry", businessId, id] as const,
  fetcher: ({ businessId, id }) =>
    getJournalEntry({ data: { businessId, id } }),
});

/**
 * Invalidate the queries that a journal-entry mutation affects beyond the JE
 * list itself: account balances, account overviews, and entity associations.
 * These are derived/aggregate data that can't be reliably patched client-side,
 * so they're invalidated (refetched) rather than optimistically updated.
 */
export function invalidateJournalEntryDependentQueries(
  queryClient: QueryClient,
  businessId: string,
) {
  return Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) && key[0] === "account" && key[1] === businessId
        );
      },
    }),
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === "account-overview" &&
          key[1] === businessId
        );
      },
    }),
    // Journal entry mutations also touch entity_association rows (delete cleans
    // up linked document/vendor/worksheet associations), so refresh any
    // associations queries for this business — e.g. the tab count badges on
    // document/vendor detail pages.
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === "association" &&
          key[1] === businessId
        );
      },
    }),
    // Journal entries drive the P&L and balance sheet reports, so invalidate
    // every report query for this business (they're aggregates that can't be
    // patched client-side).
    invalidateReportsQueries(queryClient, businessId),
  ]);
}

/**
 * Optimistically update a single journal entry across every JE list query
 * variant (all pages, sorts, searches) so the table shows fresh values
 * immediately on navigation back — even if a route loader re-seeds stale data.
 * Also invalidates the dependent account/overview/association queries.
 */
export function optimisticUpdateJournalEntryList(
  queryClient: QueryClient,
  businessId: string,
  id: string,
  patch: Partial<JournalEntryItem>,
) {
  updateListItemInCache<JournalEntryItem>(
    queryClient,
    ["journal-entry", businessId],
    id,
    patch,
  );
  return invalidateJournalEntryDependentQueries(queryClient, businessId);
}

/**
 * Invalidate every journal-entry list query variant plus the dependent
 * account/overview/association queries. Used for deletes (in-place table
 * deletes and detail-route deletes) where the row is gone and can't be
 * patched client-side.
 */
export function invalidateJournalEntryListQueries(
  queryClient: QueryClient,
  businessId: string,
) {
  return Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === "journal-entry" &&
          key[1] === businessId &&
          typeof key[2] === "number"
        );
      },
    }),
    invalidateJournalEntryDependentQueries(queryClient, businessId),
  ]);
}
